// libs
import get from 'lodash/get';

// helpers
import { getAdPlatformEventURL, ROUTES } from 'shared/api/urls';
import { log } from 'shared/helpers/SentryLogger';
import { TAG_IDENTIFIER } from 'shared/helpers/SentryLogger/constants';
import { objectValues } from 'shared/helpers/utils/objects';

// api
import { ApiHelper } from 'shared/helpers/api';

// defs
import { IWidgetTracking } from 'shared/components/AdPlatformWidgets/types';
import { ITrackingCommonFields, ITrackingBody } from './types';

const MODULE_IDENTIFIER = 'Impression Tracking';

let EVENTS: { [key: string]: IWidgetTracking } = {};
let COMMON: Pick<
  ITrackingCommonFields,
  'platform' | 'vertical' | 'app_version' | 'session_id' | 'source' | 'customer_id'
> = {
  platform: 'mweb',
  vertical: 'www.nykaafashion.com',
  app_version: '',
  session_id: '',
  source: 'nf-web',
  customer_id: '',
};

const getMCID = () => {
  return get(window, 's.marketingCloudVisitorID', '') || '';
};

const getCommonFields = () => ({
  ...COMMON,
  device_model: window.navigator.userAgent,
  mc_id: getMCID(),
  timestamp: new Date().getTime() / 1000,
});

const getEventsToSend = (): ITrackingBody | undefined => {
  // no events to send
  if (Object.keys(EVENTS).length === 0) {
    return;
  }

  const eventsToSend = { ...EVENTS };
  EVENTS = {};

  return {
    events: objectValues(eventsToSend),
    common_fields: getCommonFields(),
  };
};

export const setCommonConfig = ({
  platform,
  source,
  vertical,
  customer_id,
}: Pick<ITrackingCommonFields, 'platform' | 'source' | 'vertical' | 'customer_id'>) => {
  COMMON = {
    ...COMMON,
    platform,
    source,
    vertical,
    customer_id,
  };
};

export const addEvents = (trackingData: Omit<IWidgetTracking, 'impressions' | 'timestamp'>) => {
  const transaction_id = get(trackingData, 'transaction_id', '');
  const currentTimestamp = new Date().getTime() / 1000;
  if (EVENTS[transaction_id] === undefined) {
    EVENTS[transaction_id] = {
      ...trackingData,
      impressions: 0,
      timestamp: currentTimestamp,
    };
  }
  const currentImpressions = EVENTS[transaction_id].impressions;
  EVENTS[transaction_id] = {
    ...EVENTS[transaction_id],
    impressions: currentImpressions + 1,
    timestamp: currentTimestamp,
  };
};

export const sendEvents = () => {
  const body = getEventsToSend();

  if (body === undefined) {
    return;
  }

  const url = getAdPlatformEventURL(ROUTES.AD_PLATFORM_EVENTS);
  return ApiHelper(url, { method: 'POST', body, isBodyJSON: true, appendCSRFHeader: false })
    .then(() => {
      /** DO NOTHING */
    })
    .catch((error: any) => {
      log({
        error,
        tags: { [TAG_IDENTIFIER.MODULE]: MODULE_IDENTIFIER },
      });
    });
};

export const sendClickEvent = (
  trackingData: Omit<IWidgetTracking, 'impressions' | 'timestamp'>
) => {
  const body = {
    common_fields: getCommonFields(),
    events: [
      {
        ...trackingData,
        timestamp: new Date().getTime() / 1000,
      },
    ],
  };

  const url = getAdPlatformEventURL(ROUTES.AD_PLATFORM_EVENTS);
  return ApiHelper(url, { method: 'POST', body, isBodyJSON: true, appendCSRFHeader: false })
    .then(() => {
      /** DO NOTHING */
    })
    .catch((error: any) => {
      log({
        error,
        tags: { [TAG_IDENTIFIER.MODULE]: MODULE_IDENTIFIER },
      });
    });
};

export const logData = () => {
  const body = getEventsToSend();

  if (body === undefined) {
    return;
  }

  // https://stackoverflow.com/questions/40523469/navigator-sendbeacon-to-pass-header-information
  let blob = new Blob([JSON.stringify(body)], {
    type: 'text/plain; charset=UTF-8',
  });

  const url = getAdPlatformEventURL(ROUTES.AD_PLATFORM_EVENTS);
  window.navigator.sendBeacon(url, blob);
};
